import { ChangeDetectionStrategy, Component, ElementRef, inject, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  REALLOCATION_V2_FILE_UPLOAD_STEP_ENABLED,
  REALLOCATION_V2_REQUEST_NOTES_ENABLED,
  ReallocationV2Store,
} from '../../state/reallocation-v2.store';

/**
 * Optional step between Allocate to Models and Review & Submit, carrying two
 * independently-flagged sections: file attachments and a request note. The step
 * itself only exists while at least one of them is enabled.
 *
 * Neither is required — the advisor can continue having supplied nothing, which is
 * the common case. Files are held in the store and sent as the multipart part at
 * submit rather than uploaded up front, so abandoning the wizard leaves nothing
 * behind.
 */
@Component({
  selector: 'app-rav2-attach-files-step',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './attach-files-step.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttachFilesStepComponent {
  readonly store = inject(ReallocationV2Store);
  private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  readonly filesEnabled = REALLOCATION_V2_FILE_UPLOAD_STEP_ENABLED;
  readonly notesEnabled = REALLOCATION_V2_REQUEST_NOTES_ENABLED;

  readonly isDragging = { value: false };

  browse(): void {
    this.fileInput()?.nativeElement.click();
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.store.addFiles(Array.from(input.files ?? []));
    // cleared so picking the same file again still raises a change event
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging.value = true;
  }

  onDragLeave(): void {
    this.isDragging.value = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging.value = false;
    this.store.addFiles(Array.from(event.dataTransfer?.files ?? []));
  }

  remove(file: File): void {
    this.store.removeFile(file);
  }
}
